import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'sonner';

import { PaginatedResponse } from '@/lib/api';
import { toCamelCase, toSnakeCase } from '@/lib/case';
import { baseUrl } from '@/lib/config';

import { useAuthAxios } from '@/hooks/useAuthAxios';

import {
    CheckTeamsResponse,
    DbUser,
    InviteUserRequest,
    Role,
    TeamWithMembers,
    UserProfile,
} from './types';

// Query key constants
export const AUTH_QUERY_KEY = 'auth';

// Types
interface SignupRequest {
    name: string;
    username: string;
    password: string;
    role: Role;
}

interface LoginRequest {
    username: string;
    password: string;
}

export interface AuthResponse {
    accessToken: string;
    tokenType: string;
    refreshToken: string;
    accessTokenExpiresAt: string;
}

interface TwoFAResponse {
    requires2Fa: boolean;
    token: string;
}

interface VerifyTwoFARequest {
    token: string;
    otpCode: string;
}

export const useSignup = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (request: SignupRequest) => {
            const response = await axios.post(
                `${baseUrl}/api/auth/signup`,
                toSnakeCase(request),
            );
            return toCamelCase(response.data);
        },
        onSuccess: () =>
            queryClient.invalidateQueries({
                queryKey: [AUTH_QUERY_KEY, 'signup'],
            }),
        onError: (error: any) => {
            toast.error(
                `Signup failed: ${error.response?.data?.detail || error.message}`,
            );
        },
    });
};

export const useLogin = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (
            request: LoginRequest & { rememberMe?: boolean },
        ) => {
            const params = new URLSearchParams();
            params.append('username', request.username);
            params.append('password', request.password);
            params.append('remember_me', request.rememberMe ? 'true' : 'false');

            const response = await axios.post(
                `${baseUrl}/api/auth/login`,
                params,
                {
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                    },
                },
            );
            return toCamelCase<AuthResponse | TwoFAResponse>(response.data);
        },
        onSuccess: () =>
            queryClient.invalidateQueries({
                queryKey: [AUTH_QUERY_KEY, 'login'],
            }),
        onError: (error: any) => {
            toast.error(
                `Login failed: ${error.response?.data?.detail || error.message}`,
            );
        },
    });
};

export const useVerifyTwoFA = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (request: VerifyTwoFARequest) => {
            const response = await axios.post(
                `${baseUrl}/api/auth/verify-2fa`,
                toSnakeCase(request),
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                },
            );
            return toCamelCase<AuthResponse>(response.data);
        },
        onSuccess: () =>
            queryClient.invalidateQueries({
                queryKey: [AUTH_QUERY_KEY, 'verify-2fa'],
            }),
        onError: (error: any) => {
            toast.error(
                `2FA verification failed: ${error.response?.data?.detail || error.message}`,
            );
        },
    });
};

export const useUserProfile = (options?: { refetchInterval?: number }) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: [AUTH_QUERY_KEY, 'me'],
        queryFn: async () => {
            try {
                const response = await client.get(`${baseUrl}/api/auth/me`);
                return toCamelCase<UserProfile>(response.data);
            } catch (err: any) {
                return null;
            }
        },
        enabled,
        refetchInterval: options?.refetchInterval,
    });
};

export const useCreateTeam = () => {
    const queryClient = useQueryClient();

    const { client, enabled } = useAuthAxios();

    return useMutation({
        mutationFn: async (teamName: string) => {
            return client.post(`/api/teams/create`, toSnakeCase({ teamName }));
        },
        onSuccess: () =>
            queryClient.invalidateQueries({
                queryKey: ['teams', 'created'],
            }),
        onError: (error: any) => {
            toast.error(
                `Failed to create team: ${error.response?.data?.detail || error.message}`,
            );
        },
    });
};

export const useJoinTeam = () => {
    const queryClient = useQueryClient();
    const { client, enabled } = useAuthAxios();

    return useMutation({
        mutationFn: async (teamCode: string) => {
            const response = await client.post(
                `/api/teams/join`,
                toSnakeCase({ teamCode }),
            );
        },
        onSuccess: () =>
            queryClient.invalidateQueries({
                queryKey: ['teams', 'joined'],
            }),
        onError: (error: any) => {
            toast.error(
                `Failed to join team: ${error.response?.data?.detail || error.message}`,
            );
        },
    });
};

export const useInviteUser = () => {
    const queryClient = useQueryClient();
    const { client, enabled } = useAuthAxios();

    return useMutation({
        mutationFn: async ({ username, teamCode }: InviteUserRequest) => {
            const response = await client.post(
                `/api/teams/invite`,
                toSnakeCase({ username, teamCode }),
            );
        },
        onSuccess: () =>
            queryClient.invalidateQueries({
                queryKey: ['teams', 'invited'],
            }),
        onError: (error: any) => {
            toast.error(
                `Failed to invite user: ${error.response?.data?.detail || error.message}`,
            );
        },
    });
};

export const useLeaveTeam = () => {
    const queryClient = useQueryClient();
    const { client, enabled } = useAuthAxios();

    return useMutation({
        mutationFn: async () => {
            const response = await client.post(`/api/teams/leave`);
        },
        onSuccess: () =>
            queryClient.invalidateQueries({
                queryKey: ['teams', 'left'],
            }),
        onError: (error: any) => {
            toast.error(
                `Failed to leave team: ${error.response?.data?.detail || error.message}`,
            );
        },
    });
};

export const useGetTeamById = (teamId: string | null) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['teams', 'team', teamId],
        queryFn: async () => {
            const response = await client.get(`/api/teams/team/${teamId}`);
            return toCamelCase<TeamWithMembers>(response.data);
        },
        enabled: !!teamId && enabled,
    });
};

export interface TeamMembersPagination {
    limit: number;
    skip: number;
    search?: string;
    roleFilter?: 'admins' | 'pending';
}

export const useGetTeamMembers = (
    teamId: string | null,
    pagination: TeamMembersPagination,
) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['teams', 'team', teamId, 'members', pagination],
        queryFn: async () => {
            const response = await client.get(
                `/api/teams/team/${teamId}/members`,
                { params: toSnakeCase(pagination) },
            );
            return toCamelCase<PaginatedResponse<DbUser>>(response.data);
        },
        enabled: !!teamId && enabled,
    });
};

export const useUpdateUserRole = () => {
    const queryClient = useQueryClient();
    const { client, enabled } = useAuthAxios();

    return useMutation({
        mutationFn: async ({
            userId,
            role,
        }: {
            userId: string;
            role: Role;
        }) => {
            const response = await client.patch(
                `/api/teams/users/${userId}/role`,
                {
                    role: role,
                },
            );
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['teams'],
            });
        },
        onError: (error: any) => {
            toast.error(
                `Failed to update user role: ${error.response?.data?.detail || error.message}`,
            );
        },
    });
};

export const useUpdateUserRoles = () => {
    const queryClient = useQueryClient();
    const { client, enabled } = useAuthAxios();

    return useMutation({
        mutationFn: async ({
            userId,
            roles,
        }: {
            userId: string;
            roles: Role[];
        }) => {
            const response = await client.patch(
                `/api/teams/users/${userId}/roles`,
                {
                    roles: roles,
                },
            );
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['teams'],
            });
        },
        onError: (error: any) => {
            toast.error(
                `Failed to update user roles: ${error.response?.data?.detail || error.message}`,
            );
        },
    });
};

export const useDeleteUser = () => {
    const queryClient = useQueryClient();
    const { client, enabled } = useAuthAxios();

    return useMutation({
        mutationFn: async (userId: string) => {
            const response = await client.delete(`/api/teams/users/${userId}`);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['teams'],
            });
        },
        onError: (error: any) => {
            toast.error(
                `Failed to delete user: ${error.response?.data?.detail || error.message}`,
            );
        },
    });
};

export const useUnlockUser = () => {
    const queryClient = useQueryClient();
    const { client } = useAuthAxios();

    return useMutation({
        mutationFn: async (userId: string) => {
            const response = await client.post(`/api/teams/users/${userId}/unlock`);
            return toCamelCase(response.data);
        },
        onSuccess: () => {
            toast.success('User account unlocked successfully');
            queryClient.invalidateQueries({
                queryKey: ['teams'],
            });
        },
        onError: (error: any) => {
            toast.error(
                `Failed to unlock user: ${error.response?.data?.detail || error.message}`,
            );
        },
    });
};

export const useRefreshToken = () => {
    const { client } = useAuthAxios();

    return useMutation({
        mutationFn: async (refreshToken: string) => {
            const response = await client.post(
                '/api/auth/refresh',
                toSnakeCase({ refreshToken }),
            );
            return toCamelCase<AuthResponse>(response.data);
        },
    });
};

export const useCheckTeams = () => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['teams', 'check_teams'],
        queryFn: async () => {
            const response = await client.get(`/api/teams/check_teams`);
            return toCamelCase<CheckTeamsResponse>(response.data);
        },
        enabled,
    });
};
